import React, { useState, useEffect, useMemo } from 'react';
import { useContacts } from '../../hooks/useContacts';
import { useCompanies } from '../../hooks/useCompanies';

const DealForm = ({ deal = null, onSubmit, onCancel, loading = false }) => {
  const { contacts } = useContacts();
  const { companies, createCompany } = useCompanies();

  const [formData, setFormData] = useState({
    title: '',
    value: '',
    currency: 'EUR',
    stage: 'Contacts',
    category: '',
    expectedCloseDate: '',
    description: '',
    contactIds: [],
    companyId: '',
    companyName: ''
  });

  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [creatingCompanyLoading, setCreatingCompanyLoading] = useState(false);

  useEffect(() => {
    if (deal) {
      // Format closeDate for input[type="date"] (YYYY-MM-DD)
      let formattedCloseDate = '';
      if (deal.closeDate) {
        const date = deal.closeDate.toDate ? deal.closeDate.toDate() : new Date(deal.closeDate);
        formattedCloseDate = date.toISOString().split('T')[0];
      }

      setFormData({
        title: deal.title || '',
        value: deal.value || '',
        currency: deal.currency || 'EUR',
        stage: deal.stage || 'Contacts',
        category: deal.category || '',
        expectedCloseDate: formattedCloseDate,
        description: deal.description || '',
        contactIds: deal.contactIds || [],
        companyId: deal.companyId || '',
        companyName: deal._displayCache?.companyName || ''
      });
      setCompanySearchQuery(deal._displayCache?.companyName || '');
    }
  }, [deal]);

  const stages = ['Contacts', 'Proposal', 'Negotiation', 'Won', 'Lost'];
  const currencies = [
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'GBP', symbol: '£', name: 'British Pound' }
  ];

  // Company search suggestions
  const companySuggestions = useMemo(() => {
    if (!companySearchQuery || companySearchQuery.length < 2 || formData.companyId) {
      return [];
    }
    const searchTerm = companySearchQuery.toLowerCase().trim();
    return companies
      .filter(c => c.name?.toLowerCase().includes(searchTerm))
      .slice(0, 5);
  }, [companySearchQuery, companies, formData.companyId]);

  // Handle company selection from suggestions
  const handleCompanySelect = (company) => {
    setFormData(prev => ({ ...prev, companyId: company.id, companyName: company.name }));
    setCompanySearchQuery(company.name);
    setShowCompanySuggestions(false);
    setErrors(prev => ({ ...prev, companyId: undefined }));
  };

  // Clear selected company
  const handleClearCompany = () => {
    setFormData(prev => ({ ...prev, companyId: '', companyName: '' }));
    setCompanySearchQuery('');
    setShowCompanySuggestions(false);
  };

  // Start creating new company
  const handleStartCreateCompany = () => {
    setIsCreatingCompany(true);
    setNewCompanyName(companySearchQuery); // Pre-fill with search query
    setShowCompanySuggestions(false);
  };

  // Cancel creating new company
  const handleCancelCreateCompany = () => {
    setIsCreatingCompany(false);
    setNewCompanyName('');
  };

  // Create new company and select it
  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) return;

    setCreatingCompanyLoading(true);
    try {
      const newCompany = await createCompany({ name: newCompanyName.trim() });
      // Select the newly created company
      setFormData(prev => ({
        ...prev,
        companyId: newCompany.id,
        companyName: newCompany.name
      }));
      setCompanySearchQuery(newCompany.name);
      setIsCreatingCompany(false);
      setNewCompanyName('');
      setErrors(prev => ({ ...prev, companyId: undefined }));
    } catch (err) {
      console.error('Error creating company:', err);
      setErrors(prev => ({ ...prev, companyId: 'Failed to create company' }));
    } finally {
      setCreatingCompanyLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.value || formData.value <= 0) {
      newErrors.value = 'Value must be greater than 0';
    }

    if (!formData.companyId) {
      newErrors.companyId = 'Company is required';
    }

    if (formData.contactIds.length === 0) {
      newErrors.contactIds = 'At least one contact is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        ...formData,
        value: parseFloat(formData.value)
      };

      // Convert expectedCloseDate string to Date for Firestore
      if (formData.expectedCloseDate) {
        submitData.closeDate = new Date(formData.expectedCloseDate);
      } else {
        submitData.closeDate = null;
      }
      delete submitData.expectedCloseDate;

      onSubmit(submitData);
    }
  };

  const handleContactToggle = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    const isAdding = !formData.contactIds.includes(contactId);

    setFormData(prev => {
      const newContactIds = isAdding
        ? [...prev.contactIds, contactId]
        : prev.contactIds.filter(id => id !== contactId);

      // Auto-fill company from contact if not already set
      if (isAdding && contact?.companyId && !prev.companyId) {
        const linkedCompany = companies.find(c => c.id === contact.companyId);
        if (linkedCompany) {
          setCompanySearchQuery(linkedCompany.name);
          return {
            ...prev,
            contactIds: newContactIds,
            companyId: linkedCompany.id,
            companyName: linkedCompany.name
          };
        }
      }

      return { ...prev, contactIds: newContactIds };
    });
    setErrors(prev => ({ ...prev, contactIds: undefined }));
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (contact.company && contact.company.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const selectedContacts = contacts.filter(c => formData.contactIds.includes(c.id));

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Title *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => {
            setFormData({ ...formData, title: e.target.value });
            setErrors({ ...errors, title: undefined });
          }}
          className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all ${
            errors.title
              ? 'border-red-500 bg-red-50 dark:bg-red-900/10'
              : 'border-slate-300 dark:border-border-dark bg-white dark:bg-surface-dark text-slate-900 dark:text-white'
          }`}
          placeholder="e.g., Website Redesign for Acme Corp"
        />
        {errors.title && (
          <p className="text-red-500 text-xs mt-1">{errors.title}</p>
        )}
      </div>

      {/* Value and Currency */}
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Value *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.value}
            onChange={(e) => {
              setFormData({ ...formData, value: e.target.value });
              setErrors({ ...errors, value: undefined });
            }}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all ${
              errors.value
                ? 'border-red-500 bg-red-50 dark:bg-red-900/10'
                : 'border-slate-300 dark:border-border-dark bg-white dark:bg-surface-dark text-slate-900 dark:text-white'
            }`}
            placeholder="5000"
          />
          {errors.value && (
            <p className="text-red-500 text-xs mt-1">{errors.value}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Currency
          </label>
          <select
            value={formData.currency}
            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm bg-white dark:bg-surface-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          >
            {currencies.map(curr => (
              <option key={curr.code} value={curr.code}>
                {curr.symbol} {curr.code}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Company */}
      <div className="relative">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Company *
        </label>

        {formData.companyId ? (
          // Selected company display
          <div className="flex items-center gap-2 px-3 py-2 border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-[18px]">check_circle</span>
            <span className="flex-1 text-sm text-slate-900 dark:text-white font-medium">{formData.companyName}</span>
            <button
              type="button"
              onClick={handleClearCompany}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>
        ) : isCreatingCompany ? (
          // Create new company inline form
          <div className="border border-primary/30 dark:border-primary/50 bg-primary/5 dark:bg-primary/10 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-primary text-[18px]">add_business</span>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Create New Company</span>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                placeholder="Company name"
                autoFocus
                className="flex-1 px-3 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm bg-white dark:bg-surface-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateCompany();
                  } else if (e.key === 'Escape') {
                    handleCancelCreateCompany();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleCancelCreateCompany}
                disabled={creatingCompanyLoading}
                className="px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-[#1a2632] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCompany}
                disabled={creatingCompanyLoading || !newCompanyName.trim()}
                className="px-3 py-2 text-sm font-medium text-white bg-primary hover:bg-blue-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creatingCompanyLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        ) : (
          // Company search input
          <>
            <input
              type="text"
              value={companySearchQuery}
              onChange={(e) => {
                setCompanySearchQuery(e.target.value);
                setShowCompanySuggestions(true);
                setErrors(prev => ({ ...prev, companyId: undefined }));
              }}
              onFocus={() => setShowCompanySuggestions(true)}
              onBlur={() => setTimeout(() => setShowCompanySuggestions(false), 200)}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all ${
                errors.companyId
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/10'
                  : 'border-slate-300 dark:border-border-dark bg-white dark:bg-surface-dark text-slate-900 dark:text-white'
              }`}
              placeholder="Search for a company..."
            />

            {/* Company Suggestions Dropdown */}
            {showCompanySuggestions && (companySuggestions.length > 0 || companySearchQuery.length >= 2) && (
              <div className="absolute z-20 w-full mt-1 bg-white dark:bg-surface-dark border border-slate-200 dark:border-border-dark rounded-lg shadow-lg overflow-hidden">
                {companySuggestions.length > 0 && (
                  <>
                    <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-slate-200 dark:border-border-dark">
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1">
                        <span className="material-symbols-outlined text-[14px]">info</span>
                        Similar companies found
                      </span>
                    </div>
                    {companySuggestions.map(company => (
                      <button
                        key={company.id}
                        type="button"
                        onClick={() => handleCompanySelect(company)}
                        className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-[#1a2632] transition-colors flex items-center gap-3"
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {company.logoUrl ? (
                            <img src={company.logoUrl} alt="" className="w-6 h-6 rounded" />
                          ) : (
                            <span className="text-xs font-bold text-primary">
                              {company.name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{company.name}</div>
                          {company.industry && (
                            <div className="text-xs text-slate-500 dark:text-text-secondary truncate">{company.industry}</div>
                          )}
                        </div>
                        <span className="text-xs text-primary font-medium">Select</span>
                      </button>
                    ))}
                  </>
                )}
                {/* Create new company option */}
                <button
                  type="button"
                  onClick={handleStartCreateCompany}
                  className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-[#1a2632] transition-colors flex items-center gap-3 border-t border-slate-200 dark:border-border-dark"
                >
                  <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-[18px]">add</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 dark:text-white">
                      Create new company{companySearchQuery ? `: "${companySearchQuery}"` : ''}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-text-secondary">Add a new company to your CRM</div>
                  </div>
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">Create</span>
                </button>
              </div>
            )}
          </>
        )}

        {errors.companyId && (
          <p className="text-red-500 text-xs mt-1">{errors.companyId}</p>
        )}
      </div>

      {/* Stage and Expected Close Date */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Stage
          </label>
          <select
            value={formData.stage}
            onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm bg-white dark:bg-surface-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          >
            {stages.map(stage => (
              <option key={stage} value={stage}>{stage}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Expected Close Date
          </label>
          <input
            type="date"
            value={formData.expectedCloseDate}
            onChange={(e) => setFormData({ ...formData, expectedCloseDate: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm bg-white dark:bg-surface-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Category
        </label>
        <input
          type="text"
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          className="w-full px-3 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm bg-white dark:bg-surface-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          placeholder="e.g., Web Design, Consulting"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm bg-white dark:bg-surface-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
          placeholder="Add notes or details about this deal..."
        />
      </div>

      {/* Contact Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Associated Contacts *
        </label>

        {/* Selected Contacts */}
        {selectedContacts.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {selectedContacts.map(contact => (
              <span
                key={contact.id}
                className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
              >
                {contact.name}
                <button
                  type="button"
                  onClick={() => handleContactToggle(contact.id)}
                  className="hover:bg-primary/20 rounded-full p-0.5"
                >
                  <span className="material-symbols-outlined text-[14px]">close</span>
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Search Input */}
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search contacts..."
          className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all mb-2 ${
            errors.contactIds
              ? 'border-red-500 bg-red-50 dark:bg-red-900/10'
              : 'border-slate-300 dark:border-border-dark bg-white dark:bg-surface-dark text-slate-900 dark:text-white'
          }`}
        />

        {/* Contact List */}
        <div className="border border-slate-300 dark:border-border-dark rounded-lg max-h-40 overflow-y-auto">
          {filteredContacts.length === 0 ? (
            <div className="p-3 text-center text-sm text-slate-500 dark:text-text-secondary">
              No contacts found
            </div>
          ) : (
            filteredContacts.map(contact => (
              <button
                key={contact.id}
                type="button"
                onClick={() => handleContactToggle(contact.id)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-[#1a2632] transition-colors flex items-center gap-2 ${
                  formData.contactIds.includes(contact.id)
                    ? 'bg-primary/5 dark:bg-primary/10'
                    : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.contactIds.includes(contact.id)}
                  onChange={() => {}}
                  className="rounded text-primary focus:ring-primary"
                />
                <div className="flex-1">
                  <div className="font-medium text-slate-900 dark:text-white">{contact.name}</div>
                  {contact.company && (
                    <div className="text-xs text-slate-500 dark:text-text-secondary">{contact.company}</div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {errors.contactIds && (
          <p className="text-red-500 text-xs mt-1">{errors.contactIds}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm font-medium text-slate-700 dark:text-white hover:bg-slate-50 dark:hover:bg-[#233648] transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:bg-blue-600 transition-colors disabled:opacity-50 shadow-sm"
        >
          {loading ? 'Saving...' : deal ? 'Update Deal' : 'Create Deal'}
        </button>
      </div>
    </form>
  );
};

export default DealForm;
